import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { sanitizeDescription } from '@/lib/sanitizeDescription';
import { Button } from '@/components/ui/button';
import { BulkActionBar, BulkEditDialog } from '@/components/listings/BulkOps';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CollectionsManagerDialog } from '@/components/listings/CollectionsManagerDialog';
import { AmenityPicker } from '@/components/listings/AmenityPicker';
import { PhotoManagerDialog } from '@/components/listings/PhotoManagerDialog';
import { SiteContactDialog } from '@/components/listings/SiteContactDialog';
import { LeadCaptureForm } from '@/components/listings/LeadCaptureForm';
import { geocodeAddress } from '@/lib/googleMaps';
import { todayIso } from '@/lib/dateRange';
import { RangeDatePicker } from '@/components/common/DatePickers';
import {
  Search, Loader2, X, ChevronLeft, ChevronRight,
  BedDouble, Bath, Calendar, ArrowRight, Pencil, Eye, Save, Upload, Trash2, Star,
  Plus, Check, Image, Video, GripVertical, Bold, Type, Sparkles, List, ArrowLeft, Link2,
  Phone, Mail, MessageCircle, Clock, CheckCircle2, CalendarDays, PlusCircle,
  Home as HomeIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&h=900&fit=crop&q=90',
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200&h=900&fit=crop&q=90',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&h=900&fit=crop&q=90',
  'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=1200&h=900&fit=crop&q=90',
  'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200&h=900&fit=crop&q=90',
];

const AMENITY_ICONS_MAP = {
  'wifi': 'WiFi', 'snowflake': 'AC', 'utensils-crossed': 'Kitchen',
  'washing-machine': 'Washer', 'wind': 'Dryer', 'tv': 'TV', 'waves': 'Pool',
  'bath': 'Bath', 'car': 'Parking', 'dumbbell': 'Gym', 'sparkles': 'Custom',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Public landing hero ────────────────────────────────────────
// Shown only before a search has happened. After search, the compact
// search bar is rendered at the top and this whole section collapses.
function PublicHero({
  searchStart, setSearchStart,
  searchEnd, setSearchEnd,
  onSearch, searching,
  onOpenLeadForm, publicContact,
}) {
  const c = publicContact || {};
  const firstName = ((c.name || '').trim().split(/\s+/)[0]) || 'us';

  return (
    <>
      {/* Hero */}
      <section
        className="relative overflow-hidden px-4 pt-16 pb-20 sm:pt-24 sm:pb-28 text-center bg-[#F7F6F2]"
        data-testid="public-hero"
      >
        {/* Soft gradient blobs — same feel as the landing page */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-[#A8B8A5]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -right-16 h-[520px] w-[520px] rounded-full bg-[#7A9B76]/15 blur-3xl" />

        <div className="relative max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-medium tracking-[0.16em] text-[#4E6A52] uppercase bg-[#EEF2EC] rounded-full px-4 py-1.5 border border-[#D7E0D5]">
            Fully Furnished · All-Inclusive · Starting at 30+ Days
          </span>
          <h1
            className="mt-6 text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] tracking-tight text-[#2F3431]"
            style={{ fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif' }}
          >
            See what&rsquo;s available<br className="hidden sm:block" />{' '}
            <span className="italic text-[#4E6A52]">for your dates.</span>
          </h1>
          <p className="mt-5 text-[15px] sm:text-base text-[#2F3431]/70 max-w-xl mx-auto leading-relaxed">
            Select your move-in and move-out to browse open apartments.
          </p>

          {/* Search card */}
          <div className="mt-9 bg-white rounded-[28px] shadow-[0_1px_0_rgba(47,52,49,0.04),0_20px_50px_-12px_rgba(47,52,49,0.12)] border border-[#D7E0D5] p-4 sm:p-5 max-w-2xl mx-auto" data-testid="date-search">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div className="text-left px-1">
                <Label className="text-[10px] uppercase tracking-[0.14em] text-[#2F3431]/60 font-medium mb-1 block">Move In — Move Out</Label>
                <RangeDatePicker
                  start={searchStart}
                  end={searchEnd}
                  onChange={({ start, end }) => { setSearchStart(start); setSearchEnd(end); }}
                  minIso={todayIso()}
                  testId="search-date-range"
                  triggerClassName="h-14 flex items-center"
                />
              </div>
              <div className="flex items-stretch">
                <Button
                  onClick={onSearch}
                  disabled={searching}
                  className="w-full sm:w-auto h-14 rounded-2xl bg-[#4E6A52] text-white hover:bg-[#3d5541] px-6 gap-2 font-medium transition-all hover:shadow-[0_12px_32px_-8px_rgba(78,106,82,0.55)] hover:-translate-y-0.5"
                  data-testid="search-button"
                >
                  <Search className="h-4 w-4" /> {searching ? 'Searching…' : 'Check Availability'}
                </Button>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onOpenLeadForm}
            className="group mt-6 inline-flex items-center gap-1.5 text-sm text-[#2F3431]/60 hover:text-[#4E6A52] transition-colors"
            data-testid="flexible-dates-lead"
          >
            <span>Not sure on exact dates?</span>
            <span className="text-[#4E6A52] font-medium underline-offset-4 group-hover:underline">Reach out and we&rsquo;ll work with you</span>
            <ArrowRight className="h-3.5 w-3.5 text-[#4E6A52] transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </section>

      {/* Features band */}
      <section className="bg-[#EEF2EC] border-y border-[#D7E0D5]" data-testid="public-features">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid grid-cols-1 md:grid-cols-3 gap-4">
          <FeatureCard
            icon={<PlusCircle className="h-5 w-5" strokeWidth={1.5} />}
            title="All-Inclusive Pricing"
            body="One flat rate covers rent, utilities, Wi-Fi, and furnishings. No surprise bills, no setup hassle."
          />
          <FeatureCard
            icon={<CalendarDays className="h-5 w-5" strokeWidth={1.5} />}
            title="Flexible Lease Terms"
            body="Stays starting at 30 days with month-to-month flexibility. Extend or adjust as your plans change."
          />
          <FeatureCard
            icon={<CheckCircle2 className="h-5 w-5" strokeWidth={1.5} />}
            title="Easy Approval Process"
            body="Simple and straightforward. No lengthy applications — we make it easy to get settled quickly."
          />
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-[#F7F6F2]" data-testid="public-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <p className="text-[11px] sm:text-xs font-medium tracking-[0.18em] text-[#4E6A52] uppercase">Ready To Move Forward?</p>
          <h3
            className="mt-3 text-3xl sm:text-4xl lg:text-[44px] leading-[1.1] tracking-tight text-[#2F3431]"
            style={{ fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif' }}
          >
            Let&rsquo;s find <span className="italic text-[#4E6A52]">the right fit</span> for your stay.
          </h3>
          <Button
            onClick={onOpenLeadForm}
            className="mt-8 h-12 px-7 rounded-full bg-[#4E6A52] text-white hover:bg-[#3d5541] gap-2 text-[15px] transition-all hover:shadow-[0_12px_32px_-8px_rgba(78,106,82,0.55)] hover:-translate-y-0.5"
            data-testid="book-a-showing-btn"
          >
            Book a Showing <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Inline contact strip */}
      {(c.phone || c.whatsapp || c.email) && (
        <section className="bg-[#F7F6F2] pb-14" data-testid="public-hero-contact">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-sm text-[#2F3431]/70">
              Questions? Reach out to <span className="font-semibold text-[#2F3431]">{firstName}</span> directly
              {c.best_times ? <> — <span>{c.best_times}</span></> : null}.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm">
              {c.phone && (
                <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 text-[#4E6A52] hover:text-[#3d5541] transition-colors" data-testid="hero-contact-phone">
                  <Phone className="h-3.5 w-3.5" /> {c.phone}
                </a>
              )}
              {c.whatsapp && (
                <a href={`https://wa.me/${c.whatsapp.replace(/[^0-9+]/g, '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#4E6A52] hover:text-[#3d5541] transition-colors" data-testid="hero-contact-whatsapp">
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              )}
              {c.email && (
                <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 text-[#4E6A52] hover:text-[#3d5541] transition-colors" data-testid="hero-contact-email">
                  <Mail className="h-3.5 w-3.5" /> {c.email}
                </a>
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="group rounded-2xl bg-white border border-[#D7E0D5] p-6 sm:p-7 transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-16px_rgba(47,52,49,0.15)]">
      <div className="h-11 w-11 rounded-xl bg-[#EEF2EC] flex items-center justify-center text-[#4E6A52] group-hover:bg-[#4E6A52] group-hover:text-white transition-colors">
        {icon}
      </div>
      <h4 className="mt-5 text-base font-medium text-[#2F3431]">{title}</h4>
      <p className="mt-2 text-sm text-[#2F3431]/65 leading-relaxed">{body}</p>
    </div>
  );
}

/**
 * Footer rendered on the share domain (flexlivingsf.com). When in admin
 * mode we keep the simple copyright line instead to avoid leaking contact
 * info into the admin UI.
 */
function PublicContactFooter({ publicMode, contact }) {
  if (!publicMode) {
    return (
      <footer className="border-t bg-card py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">&copy; {new Date().getFullYear()} Flex Living Miami. All rights reserved.</p>
        </div>
      </footer>
    );
  }
  const c = contact || {};
  const hasAny = !!(c.phone || c.whatsapp || c.email || c.best_times || c.name);
  const hasName = !!(c.name || '').trim();
  return (
    <footer className="bg-[#EEF2EC] border-t border-[#D7E0D5] mt-12" data-testid="public-contact-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        {hasAny ? (
          <>
            <p className="text-sm text-[#2F3431]/70">
              Please reach out to {hasName ? <span className="font-semibold text-[#2F3431]">{c.name}</span> : 'us'} with any questions.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm">
              {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 text-[#4E6A52] hover:text-[#3d5541]" data-testid="public-contact-phone"><Phone className="h-4 w-4" />{c.phone}</a>}
              {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/[^0-9+]/g, '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#4E6A52] hover:text-[#3d5541]" data-testid="public-contact-whatsapp"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
              {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 text-[#4E6A52] hover:text-[#3d5541]" data-testid="public-contact-email"><Mail className="h-4 w-4" />{c.email}</a>}
              {c.best_times && <span className="inline-flex items-center gap-1.5 text-[#2F3431]/60" data-testid="public-contact-times"><Clock className="h-4 w-4" />{c.best_times}</span>}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">&copy; {new Date().getFullYear()} Flex Living Miami.</p>
        )}
      </div>
    </footer>
  );
}

function BedBathIcons({ bedrooms, bathrooms }) {
  return (
    <div className="flex items-center gap-3 mt-1.5 text-muted-foreground" data-testid="bed-bath-icons">
      {bedrooms != null && (
        <span className="flex items-center gap-1 text-sm">
          <BedDouble className="h-3.5 w-3.5" />{bedrooms === 0 ? 'Studio' : `${bedrooms} Bed`}
        </span>
      )}
      {bathrooms != null && (
        <span className="flex items-center gap-1 text-sm">
          <Bath className="h-3.5 w-3.5" />{bathrooms} Bath
        </span>
      )}
    </div>
  );
}

function ListingCard({ listing, index, searchStart, searchEnd }) {
  const allImages = listing.photos?.length > 0
    ? listing.photos.map(p => (p.url.startsWith('http') ? p.url : `${API}${p.url}`))
    : [PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]];
  // Start the carousel on the cover photo (if one is flagged) so the
  // chosen marketing shot is what shows first — without moving it from
  // its real position in the gallery.
  const coverIdx = (listing.photos || []).findIndex(p => p.is_cover);
  const [currentIdx, setCurrentIdx] = useState(coverIdx >= 0 ? coverIdx : 0);
  const goPrev = (e) => { e.preventDefault(); e.stopPropagation(); setCurrentIdx(i => (i - 1 + allImages.length) % allImages.length); };
  const goNext = (e) => { e.preventDefault(); e.stopPropagation(); setCurrentIdx(i => (i + 1) % allImages.length); };
  const detailUrl = `/listings/${listing.id}${searchStart && searchEnd ? `?move_in=${searchStart}&move_out=${searchEnd}` : ''}`;

  return (
    <div className="group bg-white rounded-3xl border border-[#D7E0D5] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-24px_rgba(47,52,49,0.22)]" data-testid="listing-card">
      <div className="aspect-[4/3] overflow-hidden bg-[#EEF2EC] relative">
        <Link to={detailUrl} data-testid="listing-card-link">
          <img src={allImages[currentIdx]} alt={listing.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" loading="lazy" decoding="async" />
        </Link>
        {allImages.length > 1 && (
          <>
            <button onClick={goPrev} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-all opacity-0 group-hover:opacity-100 z-10" data-testid="card-carousel-prev"><ChevronLeft className="h-4 w-4 text-[#4E6A52]" /></button>
            <button onClick={goNext} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-all opacity-0 group-hover:opacity-100 z-10" data-testid="card-carousel-next"><ChevronRight className="h-4 w-4 text-[#4E6A52]" /></button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
              {allImages.map((img, i) => <div key={img || `img-${i}`} className={`h-1.5 rounded-full transition-all ${i === currentIdx ? 'w-4 bg-white' : 'w-1.5 bg-white/60'}`} />)}
            </div>
          </>
        )}
      </div>
      <Link to={detailUrl}>
        <div className="p-5">
          <h3 className="text-[15px] font-medium text-[#2F3431] tracking-tight" data-testid="listing-title">{listing.title}</h3>
          <div className="mt-1 text-[#2F3431]/60">
            <BedBathIcons bedrooms={listing.bedrooms} bathrooms={listing.bathrooms} />
          </div>
          {listing.current_price != null ? (
            <p className="text-[#4E6A52] font-semibold text-lg mt-3 tabular-nums" data-testid="listing-price">
              ${listing.current_price.toLocaleString()}<span className="text-[#2F3431]/50 font-normal text-sm">/mo</span>
            </p>
          ) : (
            <p className="text-[#2F3431]/50 text-sm mt-3">Contact for pricing</p>
          )}
        </div>
      </Link>
    </div>
  );
}

function AlternativeCard({ listing, index }) {
  const cover = (listing.photos || []).find(p => p.is_cover) || (listing.photos || [])[0];
  const img = cover
    ? (cover.url.startsWith('http') ? cover.url : `${API}${cover.url}`)
    : PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length];
  const sd = listing.shift_days || 0;
  const shiftLabel = sd === 0
    ? 'Alternative dates'
    : sd > 0
      ? `${sd} day${sd === 1 ? '' : 's'} later`
      : `${Math.abs(sd)} day${Math.abs(sd) === 1 ? '' : 's'} earlier`;
  // Preserve the alt dates as query params so the detail page can echo
  // them back (e.g. in the "Back to results" link and any date-driven
  // pricing block).
  const params = new URLSearchParams();
  if (listing.alt_move_in) params.set('move_in', listing.alt_move_in);
  if (listing.alt_move_out) params.set('move_out', listing.alt_move_out);
  const detailUrl = `/listings/${listing.id}${params.toString() ? `?${params}` : ''}`;
  return (
    <Link
      to={detailUrl}
      className="flex items-center gap-4 bg-card rounded-lg border border-border/70 p-3 hover:shadow-md hover:-translate-y-0.5 hover:border-[#4E6A52]/40 transition-all cursor-pointer group"
      data-testid="alternative-card"
    >
      <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
        <img src={img} alt={listing.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform" loading="lazy" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate group-hover:text-[#4E6A52] transition-colors">{listing.title}</p>
        <BedBathIcons bedrooms={listing.bedrooms} bathrooms={listing.bathrooms} />
        <p className="text-xs text-muted-foreground mt-1">
          Available {shiftLabel}: <span className="font-medium text-foreground">{formatDate(listing.alt_move_in)}</span> <ArrowRight className="h-3 w-3 inline mx-0.5" /> <span className="font-medium text-foreground">{formatDate(listing.alt_move_out)}</span>
        </p>
      </div>
      {listing.current_price != null && (
        <p className="text-primary font-semibold text-sm tabular-nums flex-shrink-0">${listing.current_price.toLocaleString()}/mo</p>
      )}
    </Link>
  );
}

function getNext18Months() {
  const today = new Date();
  const months = [];
  for (let i = 0; i < 18; i++) {
    let m = today.getMonth() + 1 + i;
    let y = today.getFullYear();
    while (m > 12) { m -= 12; y++; }
    months.push({ year: y, month: m });
  }
  return months;
}

/* ── Rich Text Editor (for admin) ──────────────────────────────── */
function RichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current && editorRef.current) {
      editorRef.current.innerHTML = sanitizeDescription(value || '');
      isInitialMount.current = false;
    }
  }, [value]);
  const handleInput = () => { if (editorRef.current) onChange(editorRef.current.innerHTML); };
  const execCmd = (cmd) => { document.execCommand(cmd, false, null); editorRef.current?.focus(); handleInput(); };
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-muted/30">
        <button type="button" onClick={() => execCmd('bold')} className="p-1.5 rounded hover:bg-muted transition-colors" title="Bold"><Bold className="h-4 w-4" /></button>
        <div className="w-px h-5 bg-border mx-1" />
        <button type="button" onClick={() => execCmd('insertParagraph')} className="p-1.5 rounded hover:bg-muted transition-colors" title="New Paragraph"><Type className="h-4 w-4" /></button>
      </div>
      <div ref={editorRef} contentEditable onInput={handleInput} className="min-h-[100px] max-h-[200px] overflow-y-auto p-3 text-sm text-muted-foreground leading-relaxed focus:outline-none whitespace-pre-wrap [&_*]:text-inherit [&_strong]:font-semibold [&_b]:font-semibold [&_p]:mb-2 [&_p:last-child]:mb-0" />
    </div>
  );
}

/* ── Photo Lightbox ─────────────────────────────────────────── */
function PhotoLightbox({ open, onOpenChange, photos, startIdx = 0 }) {
  const [idx, setIdx] = useState(startIdx);
  useEffect(() => { if (open) setIdx(startIdx); }, [open, startIdx]);
  if (!photos?.length) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 bg-black/95 border-0 overflow-hidden" style={{ zIndex: 100 }}>
        <DialogTitle className="sr-only">Photo {idx + 1} of {photos.length}</DialogTitle>
        <div className="relative flex items-center justify-center" style={{ minHeight: '70vh' }}>
          <img src={photos[idx]?.src} alt="" className="max-h-[80vh] max-w-full object-contain" />
          {photos.length > 1 && (
            <>
              <button onClick={() => setIdx(i => (i - 1 + photos.length) % photos.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"><ChevronLeft className="h-5 w-5 text-white" /></button>
              <button onClick={() => setIdx(i => (i + 1) % photos.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"><ChevronRight className="h-5 w-5 text-white" /></button>
            </>
          )}
          <div className="absolute top-3 right-3 bg-black/50 text-white rounded-full px-3 py-1 text-xs font-medium">{idx + 1} / {photos.length}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ── Full Admin Edit Panel (for selected unit) ──────────────── */
function AdminEditPanel({ unitId, adminPass, onRefreshListings }) {
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editActiveSince, setEditActiveSince] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [settingCover, setSettingCover] = useState(null);
  const [showAmenityPicker, setShowAmenityPicker] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [priceInput, setPriceInput] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [photoManagerOpen, setPhotoManagerOpen] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const fetchListing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/public/listings/${unitId}`);
      const data = await res.json();
      setListing(data);
      setEditTitle(data.title);
      setEditDesc(data.description || '');
      setEditAddress(data.address || '');
      setEditActiveSince(data.active_since ? String(data.active_since).slice(0, 10) : '');
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [unitId]);

  useEffect(() => { if (unitId) fetchListing(); }, [fetchListing, unitId]);

  const refresh = () => { fetchListing(); onRefreshListings(); };

  const photos = (listing?.photos || []).map(p => ({ ...p, src: p.url.startsWith('http') ? p.url : `${API}${p.url}` }));

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    try {
      let lat = listing?.address_lat, lng = listing?.address_lng;
      if (editAddress && editAddress !== listing?.address) {
        try {
          const geo = await geocodeAddress(editAddress);
          if (geo) { lat = geo.lat; lng = geo.lng; }
        } catch {}
      }
      const res = await fetch(`${API}/api/public/admin/listings/${unitId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPass, title: editTitle, description: editDesc, address: editAddress, address_lat: lat, address_lng: lng, active_since: editActiveSince || null })
      });
      if (res.ok) { toast.success('Updated'); setEditing(false); refresh(); }
      else toast.error('Failed');
    } catch { toast.error('Failed'); }
    finally { setSavingDetails(false); }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      if (files.length === 1) {
        const fd = new FormData(); fd.append('file', files[0]);
        await fetch(`${API}/api/public/admin/listings/${unitId}/photos?password=${encodeURIComponent(adminPass)}`, { method: 'POST', body: fd });
      } else {
        const fd = new FormData(); files.forEach(f => fd.append('files', f));
        await fetch(`${API}/api/public/admin/listings/${unitId}/photos/batch?password=${encodeURIComponent(adminPass)}`, { method: 'POST', body: fd });
      }
      toast.success('Photo(s) uploaded'); refresh();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handlePhotoDelete = async (photoId) => {
    // Optimistic: drop the photo locally so the gallery doesn't unmount /
    // scroll back to the top while the network request is in flight.
    const prevPhotos = listing?.photos || [];
    setListing(prev => prev ? { ...prev, photos: (prev.photos || []).filter(p => p.id !== photoId) } : prev);
    try {
      const res = await fetch(`${API}/api/public/admin/listings/${unitId}/photos/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, photo_id: photoId }) });
      if (!res.ok) throw new Error('failed');
      toast.success('Removed');
      onRefreshListings();
    } catch {
      setListing(prev => prev ? { ...prev, photos: prevPhotos } : prev);
      toast.error('Failed');
    }
  };

  const handleSetCover = async (photoId) => {
    const prevPhotos = listing?.photos || [];
    // Optimistic: mark the chosen photo as cover client-side so the UI
    // updates instantly without a full re-fetch / scroll jump.
    setListing(prev => prev ? {
      ...prev,
      photos: (prev.photos || []).map(p => ({ ...p, is_cover: p.id === photoId }))
    } : prev);
    setSettingCover(photoId);
    try {
      const res = await fetch(`${API}/api/public/admin/listings/${unitId}/photos/cover`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, photo_id: photoId }) });
      if (!res.ok) throw new Error('failed');
      toast.success('Cover set');
      onRefreshListings();
    } catch {
      setListing(prev => prev ? { ...prev, photos: prevPhotos } : prev);
      toast.error('Failed');
    }
    finally { setSettingCover(null); }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = async (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); setDragOverIdx(null); return; }
    const prevPhotos = listing?.photos || [];
    const newOrder = [...prevPhotos];
    const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(dropIdx, 0, moved);
    setDragIdx(null); setDragOverIdx(null);
    // Optimistic local reorder — avoids the full re-fetch that was scrolling
    // the edit dialog back to the top mid-drag.
    setListing(prev => prev ? { ...prev, photos: newOrder } : prev);
    try {
      const res = await fetch(`${API}/api/public/admin/listings/${unitId}/photos/reorder`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, photo_ids: newOrder.map(p => p.id) }) });
      if (!res.ok) throw new Error('failed');
      onRefreshListings();
    } catch {
      setListing(prev => prev ? { ...prev, photos: prevPhotos } : prev);
      toast.error('Failed to reorder');
    }
  };

  // Reorder + auto-cover-sync for the Manage Photos dialog. Whichever
  // tile ends up at index 0 becomes the cover (we fire the reorder POST
  // first, then the set-cover POST if the top tile changed).
  const handleManagerReorder = async (newOrder) => {
    const prevPhotos = listing?.photos || [];
    setListing(prev => prev ? { ...prev, photos: newOrder } : prev);
    try {
      const res = await fetch(`${API}/api/public/admin/listings/${unitId}/photos/reorder`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, photo_ids: newOrder.map(p => p.id) }) });
      if (!res.ok) throw new Error('failed');
      const newTop = newOrder[0];
      if (newTop && !newTop.is_cover) {
        setListing(prev => prev ? {
          ...prev,
          photos: (prev.photos || []).map(p => ({ ...p, is_cover: p.id === newTop.id })),
        } : prev);
        try {
          await fetch(`${API}/api/public/admin/listings/${unitId}/photos/cover`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, photo_id: newTop.id }) });
        } catch {
          toast.error('Reorder saved, but cover sync failed');
        }
      }
      onRefreshListings();
    } catch {
      setListing(prev => prev ? { ...prev, photos: prevPhotos } : prev);
      toast.error('Failed to reorder');
    }
  };

  const [uploadProgress, setUploadProgress] = useState(null);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    // Warn the user up-front if the file is very large — proxies in front
    // of the backend frequently cap multipart uploads around 50–100 MB.
    const MAX_VIDEO_MB = 200;
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_VIDEO_MB) {
      toast.error(`Video is ${sizeMb.toFixed(1)} MB — please upload a smaller file (under ${MAX_VIDEO_MB} MB).`);
      if (videoInputRef.current) videoInputRef.current.value = '';
      return;
    }
    setUploadingVideo(true);
    setUploadProgress(0);
    try {
      // Use XHR rather than fetch so we can show real upload progress.
      // Helps the user distinguish "slow but working" from "hung".
      const url = `${API}/api/public/admin/listings/${unitId}/video?password=${encodeURIComponent(adminPass)}`;
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText });
        xhr.onerror = () => reject(new Error('network error'));
        xhr.ontimeout = () => reject(new Error('request timed out — file may be too large for the proxy'));
        xhr.timeout = 10 * 60 * 1000; // 10-minute ceiling
        const fd = new FormData(); fd.append('file', file);
        xhr.send(fd);
      });
      if (result.status >= 200 && result.status < 300) {
        toast.success('Video uploaded');
        refresh();
      } else {
        let detail = '';
        try { detail = JSON.parse(result.body)?.detail || ''; } catch { detail = result.body || ''; }
        if (result.status === 413) {
          toast.error(`Video too large for the server proxy (${sizeMb.toFixed(1)} MB). Try compressing it first.`);
        } else {
          toast.error(`Upload failed (${result.status})${detail ? `: ${String(detail).slice(0,140)}` : ''}`);
        }
        console.error('Video upload failed', result.status, detail);
      }
    } catch (err) {
      console.error('Video upload error', err);
      toast.error(`Upload failed: ${err?.message || 'network error'}`);
    } finally {
      setUploadingVideo(false);
      setUploadProgress(null);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleVideoDelete = async () => {
    try {
      await fetch(`${API}/api/public/admin/listings/${unitId}/video/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass }) });
      toast.success('Removed'); refresh();
    } catch { toast.error('Failed'); }
  };

  const handleSaveAmenities = async (amenities) => {
    try {
      await fetch(`${API}/api/public/admin/listings/${unitId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, amenities }) });
      toast.success('Amenities updated'); refresh();
    } catch { toast.error('Failed'); }
  };

  const toggleMonth = (year, month) => {
    const key = `${year}-${month}`;
    setSelectedMonths(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleSetPrice = async () => {
    if (selectedMonths.length === 0 || !priceInput) return;
    setSavingPrice(true);
    const entries = selectedMonths.map(key => { const [y, m] = key.split('-').map(Number); return { year: y, month: m, price: parseFloat(priceInput) }; });
    try {
      await fetch(`${API}/api/public/admin/pricing`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, unit_id: unitId, entries }) });
      toast.success(`Price set for ${selectedMonths.length} mo`); setSelectedMonths([]); setPriceInput(''); refresh();
    } catch { toast.error('Failed'); }
    finally { setSavingPrice(false); }
  };

  const handleDeletePrice = async (year, month) => {
    try {
      await fetch(`${API}/api/public/admin/pricing/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, unit_id: unitId, year, month }) });
      toast.success('Removed'); refresh();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  if (!listing) return <p className="text-sm text-muted-foreground text-center py-4">Failed to load listing</p>;

  return (
    <div className="space-y-6">
      {/* ── Details ─────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm flex items-center gap-1.5"><Pencil className="h-4 w-4 text-primary" />Details</h4>
          {!editing && <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="admin-edit-details-btn"><Pencil className="h-3 w-3 mr-1" />Edit</Button>}
        </div>
        {editing ? (
          <div className="space-y-3">
            <div><Label className="text-sm mb-1 block">Title</Label><Input value={editTitle} onChange={e => setEditTitle(e.target.value)} /></div>
            <div><Label className="text-sm mb-1 block">Description</Label><RichTextEditor value={editDesc} onChange={setEditDesc} /></div>
            <div><Label className="text-sm mb-1 block">Address</Label><Input value={editAddress} onChange={e => setEditAddress(e.target.value)} placeholder="123 Ocean Dr, Miami" /></div>
            <div>
              <Label className="text-sm mb-1 block">Available since override <span className="text-muted-foreground font-normal text-xs">(optional — leave blank to use the unit&apos;s &quot;Available From&quot; date)</span></Label>
              <Input type="date" value={editActiveSince} onChange={e => setEditActiveSince(e.target.value)} data-testid="edit-active-since-input" />
              <p className="text-xs text-muted-foreground mt-1">Only set this if you want the calendar &amp; financial health pages to use a DIFFERENT start date than the unit&apos;s Available From.</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveDetails} disabled={savingDetails} data-testid="save-details-button">{savingDetails ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save</Button>
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditTitle(listing.title); setEditDesc(listing.description || ''); setEditAddress(listing.address || ''); setEditActiveSince(listing.active_since ? String(listing.active_since).slice(0, 10) : ''); }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground space-y-1">
            <p><strong>Title:</strong> {listing.title}</p>
            {listing.description && (
              <div>
                <strong>Description:</strong>
                <div
                  className="whitespace-pre-wrap [&_*]:text-inherit [&_strong]:font-semibold [&_b]:font-semibold [&_p]:mb-1 [&_p:last-child]:mb-0 mt-1"
                  dangerouslySetInnerHTML={{ __html: sanitizeDescription(listing.description) }}
                />
              </div>
            )}
            {listing.address && <p><strong>Address:</strong> {listing.address}</p>}
            {listing.active_since && <p><strong>Available since:</strong> {String(listing.active_since).slice(0, 10)}</p>}
          </div>
        )}
      </div>
      <hr />

      {/* ── Amenities ──────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" />Amenities ({listing.amenities?.length || 0})</h4>
          <Button variant="outline" size="sm" onClick={() => setShowAmenityPicker(true)}><Plus className="h-3 w-3 mr-1" />Manage</Button>
        </div>
        {listing.amenities?.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {listing.amenities.map((a, i) => <span key={a.name || i} className="bg-muted rounded-full px-2.5 py-1 text-xs">{a.name}</span>)}
          </div>
        ) : <p className="text-sm text-muted-foreground">No amenities yet.</p>}
      </div>
      <hr />

      {/* ── Photos ─────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm flex items-center gap-1.5"><Image className="h-4 w-4 text-primary" />Photos ({listing.photos?.length || 0})</h4>
          <div className="flex items-center gap-2">
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}Upload
            </Button>
            <Button
              variant="default" size="sm"
              onClick={() => setPhotoManagerOpen(true)}
              disabled={(listing.photos?.length || 0) === 0}
              data-testid="open-photo-manager-btn"
            >
              <Image className="h-3 w-3 mr-1" /> Manage photos
            </Button>
          </div>
        </div>
        {listing.photos?.length > 0 ? (
          <button
            type="button"
            onClick={() => setPhotoManagerOpen(true)}
            className="w-full grid gap-2 p-2 rounded-lg border bg-card hover:bg-muted/30 transition-colors text-left"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' }}
            data-testid="photo-strip"
          >
            {listing.photos.slice(0, 12).map((photo, idx) => (
              <div
                key={photo.id}
                className="relative rounded-md overflow-hidden bg-muted"
                style={{ aspectRatio: '3 / 4' }}
              >
                <img
                  src={`${API}${photo.url}`}
                  alt={photo.filename || ''}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {idx === 0 && (
                  <span className="absolute top-1 left-1 bg-white/95 text-[#1f2933] text-[9px] font-medium px-1.5 py-0.5 rounded-full shadow-sm">
                    Cover
                  </span>
                )}
              </div>
            ))}
            {listing.photos.length > 12 && (
              <div
                className="rounded-md bg-muted flex items-center justify-center text-xs text-muted-foreground"
                style={{ aspectRatio: '3 / 4' }}
              >
                +{listing.photos.length - 12}
              </div>
            )}
          </button>
        ) : <p className="text-sm text-muted-foreground">No photos yet.</p>}
        <p className="text-[10px] text-muted-foreground">
          Click any photo to open the photo manager — drag tiles to reorder, the top-left becomes the cover.
        </p>
      </div>
      <hr />

      {/* ── Video ──────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm flex items-center gap-1.5"><Video className="h-4 w-4 text-primary" />Video</h4>
          {!listing.video && (
            <div>
              <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
              <Button variant="outline" size="sm" onClick={() => videoInputRef.current?.click()} disabled={uploadingVideo}>
                {uploadingVideo ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
                {uploadingVideo
                  ? (uploadProgress != null ? `Uploading… ${uploadProgress}%` : 'Uploading…')
                  : 'Upload'}
              </Button>
            </div>
          )}
        </div>
        {listing.video ? (
          <div className="flex items-center gap-3 p-2 rounded-lg border border-border/70 bg-card">
            <Video className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <p className="text-xs text-muted-foreground truncate flex-1">{listing.video.filename}</p>
            <button onClick={handleVideoDelete} className="text-destructive hover:text-destructive/80"><Trash2 className="h-3.5 w-3.5" /></button>
            <div>
              <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
              <button onClick={() => videoInputRef.current?.click()} className="text-xs text-muted-foreground hover:text-foreground">
                {uploadingVideo
                  ? (uploadProgress != null ? `Uploading… ${uploadProgress}%` : 'Uploading…')
                  : 'Replace'}
              </button>
            </div>
          </div>
        ) : <p className="text-sm text-muted-foreground">No video.</p>}
      </div>
      <hr />

      {/* ── Pricing ────────────────────────────────── */}
      <div className="space-y-3">
        <h4 className="font-medium text-sm flex items-center gap-1.5"><Pencil className="h-4 w-4 text-primary" />Pricing</h4>
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">Select Months ({selectedMonths.length})</Label>
          <div className="flex flex-wrap gap-1.5">
            {getNext18Months().map(({ year, month }) => {
              const key = `${year}-${month}`;
              const sel = selectedMonths.includes(key);
              return (
                <button key={key} onClick={() => toggleMonth(year, month)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-muted'}`}>
                  {MONTH_NAMES[month - 1].slice(0, 3)} '{String(year).slice(2)}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex gap-3 items-end">
          <div className="flex-1 max-w-[180px]">
            <Label className="text-sm mb-1 block">Price</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input type="number" placeholder="0" className="pl-7" value={priceInput} onChange={e => setPriceInput(e.target.value)} />
            </div>
          </div>
          <Button size="sm" onClick={handleSetPrice} disabled={savingPrice || selectedMonths.length === 0}>
            {savingPrice ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Set Price{selectedMonths.length > 0 ? ` (${selectedMonths.length})` : ''}
          </Button>
        </div>
        {listing.pricing?.length > 0 && (
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Current Pricing</Label>
            <div className="flex flex-wrap gap-1.5">
              {listing.pricing.sort((a, b) => a.year - b.year || a.month - b.month).map(p => (
                <div key={`${p.year}-${p.month}`} className="flex items-center gap-1.5 bg-muted rounded-full px-2.5 py-1 text-xs">
                  <span className="font-medium">{MONTH_NAMES[p.month - 1].slice(0, 3)} {p.year}</span>
                  <span className="text-primary font-semibold">${p.price.toLocaleString()}</span>
                  <button onClick={() => handleDeletePrice(p.year, p.month)} className="text-destructive hover:text-destructive/80 ml-0.5"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Photo Lightbox */}
      <PhotoLightbox open={lightboxOpen} onOpenChange={setLightboxOpen} photos={photos} startIdx={lightboxIdx} />
      <PhotoManagerDialog
        open={photoManagerOpen}
        onOpenChange={setPhotoManagerOpen}
        photos={listing?.photos || []}
        uploading={uploading}
        onUpload={handlePhotoUpload}
        onDelete={handlePhotoDelete}
        onReorder={handleManagerReorder}
        onPreview={(idx) => { setPhotoManagerOpen(false); setLightboxIdx(idx); setLightboxOpen(true); }}
        apiBase={API}
      />
      <AmenityPicker open={showAmenityPicker} onOpenChange={setShowAmenityPicker} currentAmenities={listing.amenities || []} onSave={handleSaveAmenities} adminPass={adminPass} zIndex={70} />
    </div>
  );
}

/* ── Admin Listing Card (for All Listings admin view) ───────── */
function AdminListingCard({ listing, index, onEdit, selected, onToggleSelect }) {
  const cover = (listing.photos || []).find(p => p.is_cover) || (listing.photos || [])[0];
  const img = cover
    ? (cover.url.startsWith('http') ? cover.url : `${API}${cover.url}`)
    : PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length];
  const isUnlisted = listing.is_listed === false;
  return (
    <div className={`bg-card rounded-xl border overflow-hidden flex relative ${selected ? 'border-emerald-500 ring-2 ring-emerald-500/30' : isUnlisted ? 'border-red-300' : 'border-border/70'}`} data-testid="admin-listing-card">
      {onToggleSelect && (
        <label
          className="absolute top-2 left-2 z-10 flex items-center justify-center w-6 h-6 rounded bg-white/95 border border-border cursor-pointer shadow-sm hover:bg-white"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            className="cursor-pointer"
            data-testid={`admin-listing-select-${listing.id}`}
          />
        </label>
      )}
      {isUnlisted && (
        <span
          className="absolute top-2 right-2 z-10 text-[10px] uppercase tracking-wider font-semibold bg-red-600 text-white px-2 py-0.5 rounded-full shadow-sm"
          data-testid="admin-listing-unlisted-tag"
        >
          Unlisted
        </span>
      )}
      <div className="w-28 h-28 bg-muted flex-shrink-0 relative">
        <img src={img} alt={listing.title} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="flex-1 min-w-0 p-3 flex flex-col justify-between">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">
            {listing.property_name || 'Unassigned'}{listing.unit_number ? ` · Unit ${listing.unit_number}` : ''}
          </p>
          <h4 className="font-medium text-sm truncate mt-0.5" data-testid="admin-listing-title">{listing.title}</h4>
          <BedBathIcons bedrooms={listing.bedrooms} bathrooms={listing.bathrooms} />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs tabular-nums text-muted-foreground">
            {listing.current_price != null ? `$${listing.current_price.toLocaleString()}/mo` : 'No price'}
          </span>
          <div className="flex items-center gap-1.5">
            <Link to={`/listings/${listing.id}?as=share`} target="_blank" rel="noreferrer" data-testid="admin-listing-view-btn">
              <Button size="sm" variant="outline" title="Open the tenant view of this listing in a new tab">
                <Eye className="h-3 w-3 mr-1" />View
              </Button>
            </Link>
            <Button size="sm" variant="outline" onClick={onEdit} data-testid="admin-listing-edit-btn">
              <Pencil className="h-3 w-3 mr-1" />Edit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
export default function ListingsPage({ publicMode = false }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [alternatives, setAlternatives] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  // Dates only persist when the user is coming BACK from a listing detail
  // (flag set in ListingDetailPage on mount). Hard refreshes / direct URL
  // entries land on a clean search.
  const resumeSearch = typeof window !== 'undefined' && sessionStorage.getItem('listings_resume_search') === '1';
  const [searchStart, setSearchStart] = useState(() => resumeSearch ? (searchParams.get('start') || '') : '');
  const [searchEnd, setSearchEnd] = useState(() => resumeSearch ? (searchParams.get('end') || '') : '');
  const [searching, setSearching] = useState(false);

  // Admin mode: automatically available on the private domain because the
  // site-wide PasswordGate has already verified the password and stashed it
  // in sessionStorage as `app_password`. On the public share domain
  // (publicMode=true) we never read/use it and hide all admin UI.
  const adminPass = publicMode ? '' : (typeof window !== 'undefined' ? sessionStorage.getItem('app_password') || '' : '');
  const adminUnlocked = !publicMode && !!adminPass;

  const [showAdminDialog, setShowAdminDialog] = useState(false);
  // Bulk-selection state for the admin "All Listings" view. Cleared
  // whenever the user leaves that view.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [allListingsForAdmin, setAllListingsForAdmin] = useState([]);
  const [showAllListingsView, setShowAllListingsView] = useState(false);
  const [adminSortBy, setAdminSortBy] = useState('property');
  const [adminFilterProperty, setAdminFilterProperty] = useState('all');
  // Status filter for the admin all-listings view: 'all' | 'listed' | 'unlisted'
  const [adminFilterStatus, setAdminFilterStatus] = useState('all');
  const [showCollectionsDialog, setShowCollectionsDialog] = useState(false);
  const [showSiteContactDialog, setShowSiteContactDialog] = useState(false);
  // Public "Book a Showing" / contact dialog (uses LeadCaptureForm in inline
  // mode). TODO(future): replace with a dedicated book-a-showing flow that
  // collects property + time-slot preferences and syncs to calendar.
  const [showLeadForm, setShowLeadForm] = useState(false);

  // Always land at the top when navigating into this page (e.g. from the
  // landing page "Book Now" / "See All Availability" buttons — otherwise
  // React Router keeps the previous scroll position).
  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  // Public-mode contact block (rendered in footer on flexlivingsf.com)
  const [publicContact, setPublicContact] = useState(null);
  useEffect(() => {
    if (!publicMode) return;
    fetch(`${API}/api/public/site-contact`)
      .then(r => r.json())
      .then(setPublicContact)
      .catch(() => {});
    // Preload the Fraunces serif so public-mode headlines match the landing.
    if (!document.getElementById('fraunces-font')) {
      const link = document.createElement('link');
      link.id = 'fraunces-font';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600&display=swap';
      document.head.appendChild(link);
    }
  }, [publicMode]);

  // Hoisted so bulk-action handlers can re-fetch after applying changes.
  // Admin view fetches BOTH listed + unlisted so the Status filter below can
  // toggle between them client-side.
  const loadAllListingsForAdmin = useCallback(() => {
    return fetch(`${API}/api/public/listings?include_unlisted=true`)
      .then(r => r.json())
      .then(d => setAllListingsForAdmin(d.listings || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (adminUnlocked) loadAllListingsForAdmin();
  }, [adminUnlocked, loadAllListingsForAdmin]);

  const fetchListings = useCallback(async (start, end) => {
    setSearching(true); setLoading(true);
    try {
      const params = new URLSearchParams();
      if (start) params.set('start_date', start);
      if (end) params.set('end_date', end);
      const res = await fetch(`${API}/api/public/listings?${params}`);
      const data = await res.json();
      setListings(data.listings || []); setAlternatives(data.alternatives || []);
      setHasSearched(true);
    } catch { console.error('Failed to load listings'); }
    finally { setLoading(false); setSearching(false); }
  }, []);

  // Auto-search ONLY when coming back from a listing detail (flag pre-set).
  // On hard refresh / direct URL entry, strip any stale ?start/&end params
  // from the address bar so the next action is a clean search.
  useEffect(() => {
    const urlStart = searchParams.get('start');
    const urlEnd = searchParams.get('end');
    const resume = sessionStorage.getItem('listings_resume_search') === '1';
    sessionStorage.removeItem('listings_resume_search');
    if (urlStart && urlEnd && resume) {
      fetchListings(urlStart, urlEnd);
    } else if (urlStart || urlEnd) {
      navigate(publicMode ? '/booknow' : '/listings', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    if (!searchStart || !searchEnd) { toast.error('Please enter both move-in and move-out dates'); return; }
    if (searchStart >= searchEnd) { toast.error('Move-out must be after move-in'); return; }
    fetchListings(searchStart, searchEnd);
  };

  const clearSearch = () => {
    setSearchStart(''); setSearchEnd('');
    setListings([]); setAlternatives([]);
    setHasSearched(false);
  };

  const refreshAfterAdminEdit = () => {
    if (hasSearched && searchStart && searchEnd) fetchListings(searchStart, searchEnd);
    loadAllListingsForAdmin();
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="listings-page" style={publicMode ? { fontFamily: '"Inter", system-ui, -apple-system, sans-serif' } : undefined}>
      <header className={`sticky top-0 z-50 backdrop-blur-md border-b ${publicMode ? 'bg-[#F7F6F2]/95 border-[#D7E0D5]' : 'bg-white/95 border-border/60'}`} data-testid="listings-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {publicMode ? (
            <a href="/" className="flex items-center gap-2 group">
              <div className="h-9 w-9 rounded-full bg-[#4E6A52] flex items-center justify-center text-white transition-transform group-hover:scale-105">
                <HomeIcon className="h-4 w-4" strokeWidth={2.2} />
              </div>
              <span className="text-[15px] font-medium tracking-tight text-[#2F3431]">Flex Living</span>
            </a>
          ) : (
            <h1 className="font-heading text-[13px] sm:text-sm font-bold text-[#0f3e2e] tracking-[0.18em] uppercase">Flex Living Miami</h1>
          )}
          {adminUnlocked ? (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="listings-back-dashboard" title="Back to dashboard">
                <ArrowLeft className="h-4 w-4 mr-1" />Dashboard
              </Button>
              <div className="h-4 w-px bg-border mx-0.5" />
              <Button variant="outline" size="sm" onClick={() => { setShowAllListingsView(v => !v); setSelectedIds(new Set()); }} data-testid="admin-see-all-listings-button">
                {showAllListingsView ? <><ArrowLeft className="h-4 w-4 mr-1" />Back</> : <><List className="h-4 w-4 mr-1" />See All Listings</>}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowCollectionsDialog(true)} data-testid="admin-custom-links-button">
                <Link2 className="h-4 w-4 mr-1" />Custom Links
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowSiteContactDialog(true)} data-testid="admin-site-contact-button">
                <Phone className="h-4 w-4 mr-1" />Site Contact
              </Button>
            </div>
          ) : publicMode ? (
            <a
              href="/"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#D7E0D5] bg-white text-[#2F3431] text-sm font-medium hover:bg-[#EEF2EC] hover:border-[#4E6A52]/40 transition-colors"
              data-testid="listings-home-button"
            >
              <HomeIcon className="h-4 w-4" strokeWidth={2.2} />
              Home
            </a>
          ) : (
            <p className="hidden sm:block text-[13px] text-[#2F3431]/60">
              Furnished apartments for professionals
            </p>
          )}
        </div>
      </header>

      {!hasSearched && !showAllListingsView && (
        <PublicHero
          searchStart={searchStart}
          setSearchStart={setSearchStart}
          searchEnd={searchEnd}
          setSearchEnd={setSearchEnd}
          onSearch={handleSearch}
          searching={searching}
          onOpenLeadForm={() => setShowLeadForm(true)}
          publicContact={publicContact}
        />
      )}

      {hasSearched && (
        <section className="px-4 text-center bg-[radial-gradient(60%_60%_at_50%_0%,rgba(45,148,140,0.08)_0%,rgba(45,148,140,0)_60%)] py-8 transition-all">
          <div className="flex flex-col sm:flex-row gap-3 items-end justify-center max-w-xl mx-auto" data-testid="date-search">
            <div className="text-left w-full sm:w-auto sm:min-w-[280px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Move In — Move Out</Label>
              <RangeDatePicker
                start={searchStart}
                end={searchEnd}
                onChange={({ start, end }) => { setSearchStart(start); setSearchEnd(end); }}
                minIso={todayIso()}
                testId="search-date-range"
              />
            </div>
            <Button onClick={handleSearch} disabled={searching} className="rounded-full px-5" data-testid="search-button">
              <Search className="h-4 w-4 mr-1" />{searching ? 'Searching...' : 'Search'}
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSearch} data-testid="clear-search"><X className="h-4 w-4" /></Button>
          </div>
        </section>
      )}

      {adminUnlocked && showAllListingsView && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-all-listings-section">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="font-heading text-2xl font-semibold tracking-tight">All Listings (Admin)</h2>
              <p className="text-sm text-muted-foreground mt-1" data-testid="admin-listings-count">
                {(() => {
                  const filtered = allListingsForAdmin
                    .filter(l => adminFilterProperty === 'all' || l.property_id === adminFilterProperty)
                    .filter(l => {
                      if (adminFilterStatus === 'listed') return l.is_listed !== false;
                      if (adminFilterStatus === 'unlisted') return l.is_listed === false;
                      return true;
                    });
                  return `${filtered.length} listing${filtered.length !== 1 ? 's' : ''}`;
                })()}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Filter by Property</Label>
                <Select value={adminFilterProperty} onValueChange={setAdminFilterProperty}>
                  <SelectTrigger className="w-[220px]" data-testid="admin-filter-property"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Properties</SelectItem>
                    {[...new Map(allListingsForAdmin
                      .filter(l => l.property_id)
                      .map(l => [l.property_id, l.property_name || 'Unnamed'])
                    ).entries()]
                      .sort((a, b) => (a[1] || '').localeCompare(b[1] || ''))
                      .map(([pid, pname]) => <SelectItem key={pid} value={pid}>{pname}</SelectItem>)
                    }
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Status</Label>
                <Select value={adminFilterStatus} onValueChange={setAdminFilterStatus}>
                  <SelectTrigger className="w-[140px]" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="listed">Listed</SelectItem>
                    <SelectItem value="unlisted">Unlisted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Sort by</Label>
                <Select value={adminSortBy} onValueChange={setAdminSortBy}>
                  <SelectTrigger className="w-[180px]" data-testid="admin-sort-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="property">Property, then Unit</SelectItem>
                    <SelectItem value="unit">Unit Number</SelectItem>
                    <SelectItem value="title">Title</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {allListingsForAdmin.length === 0 ? (
            <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" /></div>
          ) : (
            (() => {
              const unitNum = (l) => {
                const n = parseInt(l.unit_number, 10);
                return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
              };
              const filtered = (adminFilterProperty === 'all'
                ? [...allListingsForAdmin]
                : allListingsForAdmin.filter(l => l.property_id === adminFilterProperty))
                .filter(l => {
                  if (adminFilterStatus === 'listed') return l.is_listed !== false;
                  if (adminFilterStatus === 'unlisted') return l.is_listed === false;
                  return true;
                });
              filtered.sort((a, b) => {
                if (adminSortBy === 'unit') return unitNum(a) - unitNum(b) || (a.unit_number || '').localeCompare(b.unit_number || '');
                if (adminSortBy === 'title') return (a.title || '').localeCompare(b.title || '');
                const pn = (a.property_name || '').localeCompare(b.property_name || '');
                return pn !== 0 ? pn : unitNum(a) - unitNum(b);
              });

              if (adminSortBy === 'property' && adminFilterProperty === 'all') {
                const groups = new Map();
                filtered.forEach(l => {
                  const key = l.property_id || 'unassigned';
                  const name = l.property_name || 'Unassigned';
                  if (!groups.has(key)) groups.set(key, { name, items: [] });
                  groups.get(key).items.push(l);
                });
                return (
                  <div className="space-y-8">
                    {[...groups.entries()].map(([pid, grp]) => (
                      <div key={pid} data-testid={`admin-group-${pid}`}>
                        <h3 className="font-heading text-lg font-semibold mb-3 pb-2 border-b border-border/60">
                          {grp.name} <span className="text-sm font-normal text-muted-foreground">({grp.items.length})</span>
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-listings-grid">
                          {grp.items.map((l, i) => (
                            <AdminListingCard
                              key={l.id} listing={l} index={i}
                              onEdit={() => { setSelectedUnitId(l.id); setShowAdminDialog(true); }}
                              selected={selectedIds.has(l.id)}
                              onToggleSelect={() => setSelectedIds(prev => {
                                const n = new Set(prev);
                                if (n.has(l.id)) n.delete(l.id); else n.add(l.id);
                                return n;
                              })}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }

              return (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-listings-grid">
                  {filtered.map((l, i) => (
                    <AdminListingCard
                      key={l.id} listing={l} index={i}
                      onEdit={() => { setSelectedUnitId(l.id); setShowAdminDialog(true); }}
                      selected={selectedIds.has(l.id)}
                      onToggleSelect={() => setSelectedIds(prev => {
                        const n = new Set(prev);
                        if (n.has(l.id)) n.delete(l.id); else n.add(l.id);
                        return n;
                      })}
                    />
                  ))}
                </div>
              );
            })()
          )}
          {/* Bulk action bar appears when any listing is selected */}
          <BulkActionBar
            selectedIds={selectedIds}
            onClear={() => setSelectedIds(new Set())}
            adminPass={adminPass}
            busy={bulkBusy}
            setBusy={setBulkBusy}
            onBulkEdit={() => setBulkEditOpen(true)}
            onChanged={loadAllListingsForAdmin}
            listings={allListingsForAdmin}
          />
          <BulkEditDialog
            open={bulkEditOpen}
            onOpenChange={setBulkEditOpen}
            selectedIds={selectedIds}
            listings={allListingsForAdmin}
            adminPass={adminPass}
            onSaved={() => { setBulkEditOpen(false); setSelectedIds(new Set()); loadAllListingsForAdmin(); }}
          />
        </section>
      )}

      {hasSearched && !showAllListingsView && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : listings.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
              <p className="text-muted-foreground text-lg">No units available for {formatDate(searchStart)} &ndash; {formatDate(searchEnd)}</p>
              {alternatives.length > 0 && <p className="text-sm text-muted-foreground mt-2">Check alternative dates below</p>}
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">{listings.length} apartment{listings.length !== 1 ? 's' : ''} available for {formatDate(searchStart)} &ndash; {formatDate(searchEnd)}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="listings-grid">
                {listings.map((listing, idx) => <ListingCard key={listing.id} listing={listing} index={idx} searchStart={searchStart} searchEnd={searchEnd} />)}
              </div>
            </>
          )}
          {alternatives.length > 0 && (
            <div className="mt-8" data-testid="alternatives-section">
              <div className="rounded-xl bg-amber-50 border border-amber-200/80 p-5">
                <h3 className="font-heading text-base font-semibold text-amber-900 mb-1">Also available with flexible dates</h3>
                <p className="text-xs text-amber-700 mb-4">These apartments are available within 2 weeks of your requested dates.</p>
                <div className="space-y-3">
                  {alternatives.map((alt, idx) => <AlternativeCard key={`${alt.id}-${alt.shift_days}`} listing={alt} index={idx} />)}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Admin Dialog — Full Edit (no password gate; site-wide gate already verified) */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" style={{ zIndex: 60 }} data-testid="admin-pricing-section">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />Edit Listing
            </DialogTitle>
            <DialogDescription className="sr-only">Manage listing details, amenities, photos, video, and pricing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!selectedUnitId ? (
              <div>
                <Label className="text-sm font-medium mb-2 block">Select Unit to Edit</Label>
                <Select value={selectedUnitId} onValueChange={v => setSelectedUnitId(v)}>
                  <SelectTrigger data-testid="admin-unit-select"><SelectValue placeholder="Choose a unit..." /></SelectTrigger>
                  <SelectContent className="z-[70]">{allListingsForAdmin.map(l => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2 -mx-1" data-testid="admin-unit-header">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Editing</p>
                    <p className="text-sm font-semibold truncate">{allListingsForAdmin.find(l => l.id === selectedUnitId)?.title || 'Unit'}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setSelectedUnitId('')} data-testid="admin-done-button">
                    <Check className="h-3.5 w-3.5 mr-1" />Done
                  </Button>
                </div>
                <AdminEditPanel unitId={selectedUnitId} adminPass={adminPass} onRefreshListings={refreshAfterAdminEdit} />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Inline-controlled lead capture (opened from the new public hero's
          "Book a Showing" and "Not sure on exact dates" links). Also opens
          automatically on the "Ready for Miami?" CTA when not searched yet. */}
      <LeadCaptureForm mode="inline" open={showLeadForm} onOpenChange={setShowLeadForm} />
      {/* Legacy "Ready for Miami?" CTA — only shown in post-search state so
          we don't duplicate the new hero's Book-a-Showing block. */}
      {publicMode && hasSearched && <LeadCaptureForm />}
      <PublicContactFooter publicMode={publicMode} contact={publicContact} />

      <CollectionsManagerDialog
        open={showCollectionsDialog}
        onOpenChange={setShowCollectionsDialog}
        adminPass={adminPass}
        allListings={allListingsForAdmin}
      />

      <SiteContactDialog
        open={showSiteContactDialog}
        onOpenChange={setShowSiteContactDialog}
        adminPass={adminPass}
      />

      <Toaster position="bottom-right" />
    </div>
  );
}
